<cs-card>
  <cs-card-title>
    {{ LL.profile.subscriptionSection() }}
    @if (model.roleClass !== 'unknown') {
    <div title-action class="subscription-badge">
      <span class="badge-label">{{ LL.profile.subscription() }}:</span>
      <span class="badge-value">{{ badgeText }}</span>
    </div>
    }
  </cs-card-title>
  <cs-card-content>
    <cs-item-list>
      <!-- Role description (always shown) -->
      <cs-note big light>{{ model.roleInfo }}</cs-note>
      <!-- Loading state -->
      @if (model.loadingSlowData) {
      <div class="loading-row">
        <span>{{ LL.profile.refreshingStatus() }}</span>
        <cs-spinner></cs-spinner>
      </div>
      }

      <!-- Subscription info error -->
      @if (model.subscriptionInfoError && !model.loadingSlowData) {
      <cs-note big light>{{ LL.profile.subscriptionInfoError() }}</cs-note>
      }

      <!-- COACH ALREADY COVERS THEM (#4462): this person's coach is paying
           for their Premium right now AND they are paying for a subscription
           of their own on top of it. Placed above the details grid on
           purpose — it is context for everything printed below, including
           the Cancel / Manage / "managed via the App Store" controls, which
           are what answer "so how do I stop paying?".
           Who sees it, and why the note says no more than it does:
           coach-covers-membership-policy.ts. -->
      @if (model.showCoachCoversMembershipNote) {
      <cs-item-group>
        <cs-note id="profile-coach-covers-membership">{{ LL.profile.coachCoversMembership() }}</cs-note>
      </cs-item-group>
      }

      <!-- PADDLE PAUSED: the pause took effect, so access (and the paid role)
           has stopped. Rendered independently of roleClass — a paused user is
           NOT a free user and must be able to resume, which the paid-only
           blocks below would otherwise hide.
           NOT gated on loadingSlowData: isPaddlePaused reads local session
           data only, so the answer is already known. Waiting for the server
           call would show a paused subscriber an empty subscription card for
           its whole duration. The sibling suppressUpgradeBlock below reads
           the same local state and is likewise ungated. -->
      @if (model.isPaddlePaused) {
      <cs-item-group>
        <cs-note id="profile-subscription-paused">{{ LL.profile.subscriptionPaused() }}</cs-note>
      </cs-item-group>
      }

      <!-- FREE USERS: Upgrade section. Suppressed while paused — a paused
           subscriber has a subscription to resume, not one to buy. -->
      @if (model.roleClass === 'unknown' && !model.suppressUpgradeBlock) {
      <cs-item-group [title]="LL.profile.boostYourGains()">
        <cs-note big light>{{ LL.profile.upgradeToPremium() }}</cs-note>
        <div class="subscription-actions">
          <cs-button primary small id="upgrade-button" icon="premium" [label]="LL.profile.upgradeNow()"
            [disabled]="model.loadingSlowData" (click)="upgradeClick.emit()"></cs-button>
        </div>
      </cs-item-group>
      }

      <!-- PAID USERS: Subscription details grid (when applicable) -->
      @if (model.roleClass === 'paid' && !model.roleIsTemporary && (model.licenseExpiry || model.currentPlan)) {
      <cs-item-group>
        <div class="subscription-details">
          @if (subscriptionTypeLabel) {
          <div class="detail-item">
            <span class="detail-label">{{ LL.profile.subscriptionType() }}</span>
            <span class="detail-value">{{ subscriptionTypeLabel }}</span>
          </div>
          }
          @if (model.licenseExpiry) {
          <div class="detail-item">
            <!-- three-way, not two-way: a pending PAUSE has to be asked about
                 in its own right, because it leaves canCancelSubscription TRUE
                 (#4247) and the old cancel/renew split therefore labelled this
                 date "Renewal Date" — while the note below says the membership
                 pauses on it. Two different words for the same date, on screen
                 at once. Do not collapse this back into the cancel/renew pair.
                 (The comment here used to say a pending pause CLEARS that flag,
                 which is the mistake deferred finding df-b4b8eb01434d was
                 about; the code was right, its stated reason was not.) -->
            <span class="detail-label">{{ model.hasPendingPaddlePause ? LL.profile.pausesOn() :
              (model.dateRowAsksProviderAction ? LL.profile.renewalDate() : LL.profile.expiresOn())
              }}</span>
            <!-- The template does NOT choose between the two dates: each
                 sentence reads the field named after it, and which of the two
                 questions that sentence asks is decided in resolveCardDates in
                 subscription-card-model.ts, where a unit test can see it.
                 Choosing here is the mechanism behind all three #4189
                 regressions — see #4278. -->
            <span class="detail-value">{{ model.dateRowDate }}</span>
          </div>
          }
          <!-- price row hidden off-platform: it's the web/PPG price the
               store app can't act on (same anti-steering reason the action
               buttons below are hidden) -->
          @if (model.currentPlan?.priceText && !model.subscribedOffPlatform) {
          <div class="detail-item">
            <span class="detail-label">{{ LL.profile.renewalPrice() }}</span>
            <span class="detail-value">{{ model.currentPlan?.priceText }}</span>
          </div>
          }
        </div>
      </cs-item-group>
      }

      <!-- VIP/Dev/etc: Show subscription type only -->
      @if (model.roleClass !== 'unknown' && model.roleClass !== 'paid') {
      <cs-item-group>
        <div class="subscription-details">
          <div class="detail-item">
            <span class="detail-label">{{ LL.profile.subscriptionType() }}</span>
            <span class="detail-value">{{ subscriptionTypeLabel || LL.profile.perpetualLicense({ type: 'Perpetual'
              })
              }}</span>
          </div>
        </div>
      </cs-item-group>
      }

      <!-- TRIAL, PAUSE SCHEDULED: Info + resume.
           Its own arm, ahead of the active/canceled split, because a pending
           PAUSE does NOT clear canCancelSubscription (#4247) — so this note
           could never render from inside the canceled block it used to live
           in, and a pausing member was told their trial simply ends on that
           date (deferred finding df-b4b8eb01434d). Where a pause comes from at
           all when the app never offers one, and why a Retain pause lands in
           the TRIAL arms rather than the regular ones, is in
           design-docs/paddle-integration.md, "Pause: handled, never offered".

           The resume hint replaces trialCancelInfo on purpose: this member is
           not about to be charged, so "cancel now and keep Premium until the
           trial ends" describes something that is not happening to them. -->
      @if (model.roleClass === 'paid' && model.trialEnds && model.hasPendingPaddlePause) {
      <cs-item-group>
        <cs-note id="profile-trial-paused">{{ LL.profile.trialPaused({ date: model.trialEnds }) }}</cs-note>
        <cs-note>{{ LL.profile.trialResumeInfo() }}</cs-note>
      </cs-item-group>
      }

      <!-- TRIAL ACTIVE: Info -->
      @if (model.roleClass === 'paid' && model.trialEnds && model.canCancelSubscription
           && !model.hasPendingPaddlePause) {
      <cs-item-group>
        <cs-note>{{ LL.profile.trialEndsOn({ date: model.trialEnds }) }}</cs-note>
        <cs-note>{{ LL.profile.trialCancelInfo() }}</cs-note>
      </cs-item-group>
      }

      <!-- TRIAL CANCELED: Info + resume.
           The !hasPendingPaddlePause guard keeps the three trial arms mutually
           exclusive: a pause says so in its own block above. -->
      @if (model.roleClass === 'paid' && model.trialEnds && !model.canCancelSubscription
           && !model.hasPendingPaddlePause) {
      <cs-item-group>
        @if (!model.hasPendingPaddleResume) {
        <!-- same resume guard as the REGULAR CANCELED block below. -->
        <cs-note id="profile-trial-canceled">{{ LL.profile.trialCanceled({ date: model.trialEnds }) }}</cs-note>
        }
        <cs-note>{{ LL.profile.trialResumeInfo() }}</cs-note>
      </cs-item-group>
      }

      <!-- REGULAR ACTIVE: Info + switch plan.
           Hidden off-platform: these notes carry the web/PPG plan price and
           steer to a plan switch the store app can't perform (the Switch
           button is hidden too). -->
      @if (model.roleClass === 'paid' && !model.trialEnds && model.canCancelSubscription && !model.roleIsTemporary && !model.subscribedOffPlatform) {
      @switch (model.planNoteMode) {
      @case (PlanNoteMode.Pending) {
      <cs-item-group>
        <cs-note>
          {{ LL.profile.pendingPlanChange({ plan: model.pendingPlanChange.toTitle, date: model.planChangeNoteDate }) }}
        </cs-note>
      </cs-item-group>
      }
      @case (PlanNoteMode.Saving) {
      <cs-item-group>
        <cs-note>
          {{ LL.profile.currentPlanWithSaving({ current: model.currentPlan.title, other: model.otherPlan.title, price:
          model.otherPlan.priceText, saving: model.otherPlan.saving }) }}
        </cs-note>
      </cs-item-group>
      }
      @case (PlanNoteMode.Plain) {
      <cs-item-group>
        <cs-note>
          {{ LL.profile.currentPlan({ current: model.currentPlan.title, other: model.otherPlan.title, price: model.otherPlan.priceText
          }) }}
        </cs-note>
      </cs-item-group>
      }
      }
      }

      <!-- TEMPORARY: Payment processing -->
      @if (model.roleClass === 'paid' && model.roleIsTemporary) {
      <cs-item-group>
        <cs-note>{{ LL.profile.paymentProcessing() }}</cs-note>
        <cs-note>{{ LL.profile.paymentProcessingHelp() }}</cs-note>
      </cs-item-group>

      }

      <!-- PAYMENT FAILING: the renewal charge did not go through and the
           payment provider is retrying. The membership is still live (so
           Cancel stays available — #4247), but the renewal date shown above
           only holds if a retry succeeds, and this note says so. Suppressed
           alongside the "ends on" note when we could not read the membership
           at all: quoting a date there would contradict the problem note. -->
      @if (model.roleClass === 'paid' && !model.roleIsTemporary && !model.loadingSlowData && model.isPaymentFailing
           && !model.showWebMembershipProblemNote) {
      <cs-item-group>
        <cs-note id="profile-payment-failing">{{ LL.profile.paymentFailing({ date: model.paymentFailingNoteDate }) }}</cs-note>
      </cs-item-group>
      }

      <!-- REGULAR, PAUSE SCHEDULED: Info + resume.
           Its own arm for the same reason as the trial pause block above: a
           pending PAUSE leaves canCancelSubscription true (#4247), so this
           note could never render from inside the REGULAR CANCELED block it
           used to live in (deferred finding df-b4b8eb01434d). The member is
           still active, so the plan notes above still apply to them; the one
           thing missing was being told about the pause at all.

           Carries the same !loadingSlowData and !showWebMembershipProblemNote
           guards as the canceled block below, and for the same reasons: both
           are about not quoting a date we cannot yet support. -->
      @if (model.roleClass === 'paid' && !model.trialEnds && model.hasPendingPaddlePause && !model.roleIsTemporary
           && !model.loadingSlowData && !model.showWebMembershipProblemNote) {
      <cs-item-group>
        <cs-note id="profile-subscription-pauses-on">{{ LL.profile.subscriptionPausesOn({ date: model.pauseNoteDate }) }}</cs-note>
        @if (model.canResumeHere) {
        <cs-note>{{ LL.profile.resubscribeInfo() }}</cs-note>
        }
      </cs-item-group>
      }

      <!-- REGULAR CANCELED: Info + resume.
           Gated on !loadingSlowData deliberately: canCancelSubscription is
           seeded from cached auth before the refresh lands, and for a Paddle
           membership in dunning that cached value says "canceled" — so
           without the gate that member would read "your subscription ends
           on <date>" for the first second, which is simply wrong. The cost
           is that a genuinely canceled member sees this note a beat later
           instead of instantly. Showing nothing briefly beats showing
           something false (#4247).

           Also suppressed when showWebMembershipProblemNote is on: in that
           state we could not read the membership at all, so claiming it
           "ends on <date>" is a statement we cannot support. The problem
           note is the honest one there. -->
      @if (model.roleClass === 'paid' && !model.trialEnds && !model.canCancelSubscription && !model.roleIsTemporary
           && !model.hasPendingPaddlePause
           && !model.loadingSlowData && !model.showWebMembershipProblemNote) {
      <cs-item-group>
        @if (!model.hasPendingPaddleResume) {
        <!-- Guarded on hasPendingPaddleResume because this branch also serves
             every NON-Paddle canceled sub, so it cannot simply test for a
             pending cancel. A pending RESUME must not be captioned "ends on"
             - see pendingPaddleChange in subscription-card-model.ts for why
             that should be unreachable anyway. -->
        <cs-note id="profile-subscription-ends">{{ LL.profile.subscriptionEnds({ date: model.subscriptionEndsNoteDate }) }}</cs-note>
        }
        <!-- resume hint shown only when an in-app Resume action exists here
             (canResumeHere). Web-billed (PayPro) subs resume via our server
             on any platform, so the hint shows in the native app too (#3826);
             store subs on the wrong platform have no in-app resume, so the
             "managed elsewhere" note is the real guidance instead. Also gate
             on !loadingSlowData so it doesn't flash on a cold load before
             subManager.provider is known -->
        @if (!model.loadingSlowData && model.canResumeHere) {
        <cs-note>{{ LL.profile.resubscribeInfo() }}</cs-note>
        }
      </cs-item-group>
      }

      <cs-item-group>
        <!-- ACTION BUTTONS (context-dependent) -->
        <div class="subscription-actions">
          <!-- Switch plan (any provider, only if manageable from this app) -->
          @if (model.canSwitchPlan && !model.subscribedOffPlatform && !model.loadingSlowData && model.otherPlan.saving) {
          <cs-button primary small
            [label]="LL.profile.switchToPlan({ plan: model.otherPlan.title })"
            (click)="switchPlanClick.emit()"></cs-button>
          }
          @if (model.canSwitchPlan && !model.subscribedOffPlatform && !model.loadingSlowData && !model.otherPlan.saving) {
          <cs-button secondary small
            [label]="LL.profile.switchToPlan({ plan: model.otherPlan.title })"
            (click)="switchPlanClick.emit()"></cs-button>
          }

          <!-- Resume subscription. PayPro: server-driven re-subscribe,
               reachable on any platform (#3826). Apple: native StoreKit
               sheet to flip auto-renew back on, iOS only. Google Play
               excluded: the cancellation model differs and we don't
               have a tested in-app resume path. canResumeHere encodes
               that provider/platform rule. Shown for a canceled sub
               whether or not it is still in its trial — a user who
               canceled during the trial must still be able to resume
               in-app (the trial-canceled note above gives the context).
               showResumeButton also stays hidden when the web-membership
               problem note is on — there onResumeClick's fallback would push
               a member who already pays us to the Subscribe page to buy a
               SECOND membership (#4247). -->
          @if (model.showResumeButton) {
          <cs-button primary small id="profile-resume-button" [label]="LL.profile.resumeSubscription()" [disabled]="model.loadingSlowData"
            (click)="resumeClick.emit()"></cs-button>
          }

          <!-- There is deliberately NO Pause button (#4189). Why, and why the
               paused notes above and Resume still exist:
               design-docs/paddle-integration.md, "Pause: handled, never
               offered". -->

          <!-- Manage subscription: PayPro sends a login email; Paddle opens
               the hosted customer portal. Only when manageable here (web). -->
          @if (model.canShowManageSubscription) {
          <cs-button secondary small [label]="LL.profile.manageSubscription.title()" [disabled]="model.loadingSlowData"
            (click)="manageSubscriptionClick.emit()"></cs-button>
          }

          <!-- Web-billed membership the server can't act on (a Paddle
               record with no stored subscription id). Without this the
               panel renders empty: no Cancel, and no "managed elsewhere"
               note either. Tell the user something is wrong and where the
               membership is billed, instead of nothing (#4247). -->
          @if (model.showWebMembershipProblemNote) {
          <cs-note id="profile-web-membership-problem">{{ LL.profile.webMembershipProblem() }}</cs-note>
          }

          <!-- Membership lives on another channel and there is no in-app
               cancel/resume here — note only. For PayPro on the native app
               an in-app action always exists (Cancel or Resume), so this
               note no longer shows there (#3826); it remains for store subs
               viewed on the wrong platform. -->
          @if (model.showOffPlatformNote && model.offPlatformNote; as note) {
            @if (note.kind === 'store') {
            <cs-note>{{ LL.profile.subscribedViaOtherPlatform({ provider: note.storeName }) }}</cs-note>
            } @else {
            <cs-note>{{ LL.profile.subscribedViaWeb() }}</cs-note>
            }
          }
        </div>
      </cs-item-group>
    </cs-item-list>


  </cs-card-content>
</cs-card>
